/**
 * **L'historique des parties** ⟦A-97 §1.1⟧ — toutes tes chaînes, ce qui les a tuées, et ce
 * qu'elles avaient atteint.
 *
 * > Mathieu, 23/08 : *« il faudra un menu pour gérer l'abandon, gérer la liste des parties qui
 * > sont Game Over et abandonnées. Genre un historique des parties. »*
 *
 * C'est l'écran `/memorial` que SPEC-UI §3.7 promet depuis le début (« `/memorial` liste tous
 * les mémoriaux du compte »), sous son nom français et enrichi de ce qu'A-97 demande en plus :
 * la durée simulée et les abonnés **dans la liste**, pas seulement au dépliage.
 *
 * ## Ce que cet écran n'est pas
 *
 * **Ce n'est pas une corbeille.** ⟦PLAN §4.11⟧ : « une run n'est JAMAIS supprimée ». Il n'y a
 * donc aucun bouton de suppression ici, et il n'existe aucune route serveur pour en poser un —
 * c'est vérifié par test (`historique.test.ts` : `DELETE` rend 404 sur les trois chemins
 * plausibles). Une chaîne finie se relit, elle ne se jette pas.
 *
 * **Ce n'est pas le classement.** Le Palmarès garde le classement global ; cet écran ne parle
 * que de TES chaînes. Les deux vivaient dans un seul panneau, et mélangeaient une lecture
 * intime (mes morts) avec une comparaison publique (les meilleurs) — chacun a maintenant sa
 * porte, et chaque écran renvoie vers l'autre.
 *
 * ## Trois choix, et ils tiennent le reste
 *
 * 1. **La liste se peuple en UNE requête.** `GET /api/me` porte depuis ⟦A-97⟧ un bloc
 *    `resume` par run (abonnés, jours simulés, vidéos) : afficher vingt lignes chiffrées ne
 *    coûte plus vingt `detailRun`. Le mémorial, lui, reste à la demande — l'épitaphe n'est
 *    pas dans l'index, et charger vingt pierres tombales pour en lire une serait payer
 *    vingt requêtes pour rien.
 * 2. **L'abandon ne s'offre que pour la chaîne EN COURS DE PARTIE**, celle que le client tient
 *    vraiment (`runStore`). Un compte peut avoir **deux** runs jouables (une par mode, §5.2) :
 *    un bouton sur l'autre appellerait `clientDeJeu().abandonner()`, qui ne connaît que la
 *    run chargée — et fermerait la mauvaise chaîne, en silence. Le jour où l'on voudra
 *    abandonner une run non chargée, il faudra un chemin qui prenne l'id, pas ce bouton-ci.
 * 3. **Après l'abandon, on reste ici.** Le routeur bascule sur son jeu de routes « sans run »
 *    dès que `run` passe à `null` — et cet écran y est monté, exactement comme le Palmarès.
 *    La ligne se recharge et devient « 🚪 Abandonnée » sous les yeux du joueur : c'est la
 *    boucle entière, visible d'un seul écran.
 *
 * Aucune règle de jeu ici : cet écran ne fait que **lire** ce que le serveur a déjà décidé.
 *
 * ⟦C57 · lot 5d · A-174⟧ **Moins de texte.** Le préambule, les listes vides et les dialogues de
 * l'abandon tenaient en phrases de 20 à 40 mots : l'écran n'en garde que la version courte
 * (`historique-court.ts`, mesurée par un test) ; la version d'avant est derrière « 📰 Les dessous »
 * du panneau, mot pour mot. L'épitaphe d'un mémorial reste à l'écran : c'est la récompense.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ErreurReseau } from "../../api/http";
import { detailRun, me, type ReponseDetailRun, type RunApi } from "../../api/jeu";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { modeActif } from "../../game/client";
// ⚠️ `libelleFin` répond à `ended_reason` (5 fins d'aujourd'hui, et les 3 d'avant le grand ménage ⟦C60⟧,
// gardées pour relire les anciennes parties : une partie neuve ne les porte plus). Il existe une AUTRE constante `FINS`
// dans `screens/Fins/Victoire.tsx` : elle décrit les 3 fins de VICTOIRE. Voir `game/fins.ts`.
import { libelleFin, libelleVictoire } from "../../game/fins";
import { nombre } from "../../game/format";
import { historiqueLocal } from "../../game/historiqueLocal";
import { dialog } from "../../stores/uiStore";
import { useRunStore } from "../../stores/runStore";

import {
  ABANDON_ECHEC_COURT,
  ABANDON_ECHEC_LONG,
  ABANDON_ECHEC_TITRE,
  ABANDON_LONG,
  AUCUNE_BAC_COURT,
  AUCUNE_BAC_LONG,
  AUCUNE_COURT,
  AUCUNE_FINIE_COURT,
  AUCUNE_FINIE_LONG,
  AUCUNE_LONG,
  PREAMBULE_BAC_COURT,
  PREAMBULE_BAC_LONG,
  PREAMBULE_COURT,
  PREAMBULE_LONG,
  corpsAbandon,
} from "./historique-court";

/** Le mémorial d'une run, tel que §5.6 le publie. `null` tant que la run n'est pas finie. */
type Memorial = ReponseDetailRun["memorial"];

export function Historique() {
  const surCompte = modeActif() === "compte";
  const naviguer = useNavigate();
  const runCourante = useRunStore((s) => s.run);
  const abandonner = useRunStore((s) => s.abandonner);

  const [runs, setRuns] = useState<RunApi[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Mémoriaux déjà chargés, par id. `null` = chargé et vide (la run n'est pas finie). */
  const [memoriaux, setMemoriaux] = useState<Record<string, Memorial>>({});
  const [ouverte, setOuverte] = useState<string | null>(null);

  const charger = useCallback(() => {
    if (!surCompte) return;
    me()
      .then((r) => setRuns(r.runs))
      .catch((e: unknown) => setErreur(messageErreur(e)));
  }, [surCompte]);

  useEffect(() => {
    charger();
  }, [charger]);

  const ouvrir = useCallback(
    (id: string) => {
      setOuverte((actuelle) => (actuelle === id ? null : id));
      if (memoriaux[id] !== undefined) return; // déjà chargé : on ne repaie pas la requête
      detailRun(id)
        .then((d) => setMemoriaux((m) => ({ ...m, [id]: d.memorial })))
        .catch((e: unknown) => setErreur(messageErreur(e)));
    },
    [memoriaux],
  );

  /**
   * ⟦A-97 §1.2⟧ Abandonner depuis l'historique — même route, même dialogue, même vérité qu'aux
   * Réglages : la chaîne rejoint le mémorial, rien n'est supprimé, et on ne revient pas dessus.
   *
   * ⚠️ On recharge la liste **après** la réponse du serveur, jamais avant : c'est elle qui fait
   * foi. L'abandon n'écrit pas `run_state` — l'état de la run garde `status: "active"` dans son
   * JSON — donc seul `runs.status`, relu par `GET /api/me`, dit la vérité.
   */
  async function abandonnerLaCourante(nom: string) {
    const ok = await dialog.confirm({
      title: "Abandonner cette chaîne ?",
      body: corpsAbandon(nom),
      confirmLabel: "Abandonner pour de bon",
      cancelLabel: "Continuer à jouer",
      danger: true,
    });
    if (!ok) return;
    try {
      await abandonner();
    } catch {
      await dialog.info({
        title: ABANDON_ECHEC_TITRE,
        body: ABANDON_ECHEC_COURT,
        confirmLabel: "Compris",
        icone: "⚠️",
      });
      return;
    }
    charger();
  }

  if (!surCompte) return <HistoriqueBacASable />;

  const finies = (runs ?? []).filter((r) => r.status === "ended");
  const vivantes = (runs ?? []).filter((r) => r.status !== "ended");

  return (
    <Panneau titre="📜 Historique des parties" large>
      {erreur ? <p className="palmares-erreur">{erreur}</p> : null}

      <p className="historique-preambule">
        {PREAMBULE_COURT}{" "}
        {/* ⟦C57 · lot 5d⟧ Le préambule d'avant, les listes vides, l'abandon : derrière le bouton. */}
        <LesDessous titre="Lire l'historique">
          <DessousBloc titre="Tes chaînes">
            <p className="dessous-texte">{PREAMBULE_LONG}</p>
          </DessousBloc>
          <DessousBloc titre="Quand la liste est vide">
            <p className="dessous-texte">{AUCUNE_LONG}</p>
            <p className="dessous-texte">{AUCUNE_FINIE_LONG}</p>
          </DessousBloc>
          <DessousBloc titre="Abandonner">
            <p className="dessous-texte">{ABANDON_LONG}</p>
            <p className="dessous-texte">{ABANDON_ECHEC_LONG}</p>
          </DessousBloc>
        </LesDessous>
      </p>

      {runs === null ? (
        <p className="palmares-vide">On récupère tes chaînes…</p>
      ) : runs.length === 0 ? (
        <p className="palmares-vide">{AUCUNE_COURT}</p>
      ) : (
        <>
          {vivantes.length > 0 ? (
            <>
              <h2 className="bloc-titre palmares-titre">
                En cours
                <span className="compteur">{vivantes.length}</span>
              </h2>
              <ul className="palmares-liste">
                {vivantes.map((run) => (
                  <LigneHistorique
                    key={run.id}
                    run={run}
                    ouverte={ouverte === run.id}
                    memorial={memoriaux[run.id]}
                    onOuvrir={() => ouvrir(run.id)}
                    // ⟦A-97 §1.2⟧ La chaîne EN COURS DE PARTIE, pas « une chaîne active » :
                    // le client de jeu ne sait abandonner que celle qu'il a chargée.
                    onAbandonner={
                      runCourante?.runId === run.id
                        ? () => void abandonnerLaCourante(run.channel_name)
                        : null
                    }
                  />
                ))}
              </ul>
            </>
          ) : null}

          <h2 className="bloc-titre bloc-titre-espace palmares-titre">
            Au mémorial
            {finies.length > 0 ? <span className="compteur">{finies.length}</span> : null}
          </h2>
          {finies.length === 0 ? (
            <p className="palmares-vide">{AUCUNE_FINIE_COURT}</p>
          ) : (
            <ul className="palmares-liste">
              {finies.map((run) => (
                <LigneHistorique
                  key={run.id}
                  run={run}
                  ouverte={ouverte === run.id}
                  memorial={memoriaux[run.id]}
                  onOuvrir={() => ouvrir(run.id)}
                  onAbandonner={null}
                />
              ))}
            </ul>
          )}
        </>
      )}

      <div className="historique-portes">
        {/* Le chemin direct vers une nouvelle partie qu'A-97 §1.2 demande. Il n'apparaît que
            s'il mène quelque part : avec une run en cours, `/nouvelle-run` renvoie à la
            chambre (routeur), et un bouton qui ramène d'où l'on vient est un bouton mort. */}
        {runCourante === null ? (
          <button
            type="button"
            className="bouton primaire"
            onClick={() => naviguer("/nouvelle-run", { replace: true })}
          >
            🎬 Lancer une nouvelle chaîne
          </button>
        ) : null}
        <button type="button" className="bouton discret" onClick={() => naviguer("/palmares")}>
          🏛 Voir le classement
        </button>
      </div>
    </Panneau>
  );
}

/**
 * ⟦A-97 §3⟧ **L'historique du bac à sable — minimal, et il assume de l'être.**
 *
 * > A-97 §3 : « Le bac à sable a ses runs locales : un historique minimal y suffit, **sans
 * > mémorial étendu**. »
 *
 * Pas de serveur, donc pas de `GET /api/me`, pas d'épitaphe, pas de dépliage : six champs par
 * fiche, lus dans une clé `localStorage` distincte de celle de la partie (`game/historiqueLocal
 * .ts`). C'est ce qui permet à l'abandon de tenir sa promesse dans les deux sens à la fois — la
 * partie est vraiment effacée du navigateur, et sa fiche reste ici.
 *
 * Ce qui n'y est PAS, et qui se dit plutôt que de se cacher : la partie en cours n'y figure pas
 * (elle n'est pas finie), et une fiche ne se rejoue pas. L'écran d'avant se contentait de dire
 * « le bac à sable ne garde rien » — c'était vrai, et ça ne l'est plus.
 *
 * La liste est lue **une fois au montage** : rien ne l'écrit pendant qu'on la regarde (la
 * seule écriture est une fin de partie, et on n'en joue pas depuis cet écran).
 */
function HistoriqueBacASable() {
  const naviguer = useNavigate();
  const runCourante = useRunStore((s) => s.run);
  const [fiches] = useState(() => historiqueLocal());

  return (
    <Panneau titre="📜 Historique des parties" large>
      <p className="historique-preambule">
        {PREAMBULE_BAC_COURT}{" "}
        <LesDessous titre="Lire l'historique">
          <DessousBloc titre="Le bac à sable">
            <p className="dessous-texte">{PREAMBULE_BAC_LONG}</p>
          </DessousBloc>
          <DessousBloc titre="Quand la liste est vide">
            <p className="dessous-texte">{AUCUNE_BAC_LONG}</p>
          </DessousBloc>
        </LesDessous>
      </p>

      {fiches.length === 0 ? (
        <p className="palmares-vide">{AUCUNE_BAC_COURT}</p>
      ) : (
        <ul className="palmares-liste">
          {fiches.map((f) => (
            <li className="palmares-ligne historique-ligne" key={f.runId}>
              <div className="palmares-tete">
                <span className="palmares-nom">{f.channelName}</span>
                <span className={`etiquette ${f.mode === "hardcore" ? "etiquette-bac" : ""}`}>
                  {f.mode === "hardcore" ? "hardcore" : "normal"}
                </span>
                {/* Le MÊME vocabulaire que côté compte : les fiches rangent une `ended_reason`,
                    pas un `GameOverId` du moteur — un seul jeu de libellés à l'écran. */}
                <span className="palmares-fin">{libelleFin(f.endedReason) ?? "Terminée"}</span>
                <span className="palmares-date">{dateCourte(f.finieLe)}</span>
              </div>
              <p className="historique-chiffres">
                {nombre(f.subs)} abonnés · J0 — J{nombre(f.daysSimulated)} ·{" "}
                {nombre(f.videosCount)} vidéo{f.videosCount > 1 ? "s" : ""}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className="historique-portes">
        {runCourante === null ? (
          <button
            type="button"
            className="bouton primaire"
            onClick={() => naviguer("/nouvelle-run", { replace: true })}
          >
            🎬 Lancer une nouvelle chaîne
          </button>
        ) : null}
      </div>
    </Panneau>
  );
}

/**
 * Une chaîne dans la liste.
 *
 * Le `resume` est **optionnel par contrat** (il n'existe que sur l'index de `GET /api/me`) :
 * absent, on n'affiche pas de ligne de chiffres plutôt que des zéros. Un « 0 abonné · J0 » sur
 * une chaîne qui a vécu deux ans serait un mensonge tranquille, et c'est exactement le genre
 * que ce projet traque.
 */
function LigneHistorique({
  run,
  ouverte,
  memorial,
  onOuvrir,
  onAbandonner,
}: {
  run: RunApi;
  ouverte: boolean;
  memorial: Memorial | undefined;
  onOuvrir(): void;
  onAbandonner: (() => void) | null;
}) {
  const finie = run.status === "ended";
  const fin = libelleFin(run.ended_reason);
  const victoire = libelleVictoire(run.victory_ending);

  return (
    <li className={`palmares-ligne historique-ligne ${finie ? "" : "vivante"}`}>
      <div className="palmares-tete">
        <span className="palmares-nom">{run.channel_name}</span>
        <span className={`etiquette ${run.mode === "hardcore" ? "etiquette-bac" : ""}`}>
          {run.mode === "hardcore" ? "hardcore" : "normal"}
        </span>
        <span className="palmares-fin">
          {finie ? (fin ?? "Terminée") : "▶ en cours"}
          {victoire ? ` · ${victoire}` : ""}
        </span>
        <span className="palmares-date">{dateCourte(run.created_at)}</span>
        {finie ? (
          <button
            className="bouton discret"
            type="button"
            aria-expanded={ouverte}
            onClick={onOuvrir}
          >
            {ouverte ? "Replier" : "Lire le mémorial"}
          </button>
        ) : null}
        {onAbandonner ? (
          <button className="bouton discret" type="button" onClick={onAbandonner}>
            🚪 Abandonner
          </button>
        ) : null}
      </div>

      {/* ⟦A-97 §1.1⟧ « leur durée simulée, leurs abonnés » — dans la LISTE, sans dépliage et
          sans requête par ligne. C'est tout l'objet du champ additif `resume`. */}
      {run.resume ? (
        <p className="historique-chiffres">
          {nombre(run.resume.subs)} abonnés · J0 — J{nombre(run.resume.days_simulated)} ·{" "}
          {nombre(run.resume.videos_count)} vidéo{run.resume.videos_count > 1 ? "s" : ""}
        </p>
      ) : null}

      {ouverte ? (
        memorial === undefined ? (
          <p className="palmares-vide">On déterre la pierre tombale…</p>
        ) : memorial === null ? (
          <p className="palmares-vide">Cette chaîne n'a pas de mémorial : elle n'est pas finie.</p>
        ) : (
          <div className={`pierre-tombale ${memorial.screen === "victoire" ? "victoire" : ""}`}>
            <p className="tombale-epitaphe">{memorial.epitaph}</p>
            <p className="tombale-dates">
              J0 — J{nombre(memorial.days_simulated)} · {nombre(memorial.subs_final)} abonnés ·{" "}
              {nombre(memorial.videos_count)} vidéos
            </p>
            <p className="tombale-fin">
              {memorial.screen === "victoire" ? "Une rue porte son nom." : "Internet t'a oublié."}
            </p>
          </div>
        )
      ) : null}
    </li>
  );
}

/** `created_at` est un epoch ms (SPEC-API §3.2). Affichage seul — aucune horloge de jeu ici. */
function dateCourte(epochMs: number): string {
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short" }).format(
    new Date(epochMs),
  );
}

/** Le `code` est la vérité, jamais le message (`api/http.ts`) — mais ici on ne décide rien. */
function messageErreur(e: unknown): string {
  if (e instanceof ErreurReseau) return "Le serveur ne répond pas. Réessaie dans un instant.";
  return "L'historique n'a pas pu être chargé.";
}
